import { get, noop } from 'lodash-es';
import { createContext, PropsWithChildren, useEffect, useState } from 'react';

import { useLocalStorage } from '@hook/store/use-local-storage.hook';
import { isString } from '@util/data/is-exist';

import { useExplorerVarsHeader } from './components/explorer-vars-header/explorer-vars-header.hook';
import { useLoadSchema } from './hooks/use-load-schema.hook';
import { ExplorerContextInterface } from './interface/explorer-data.interface';

const initialState: ExplorerContextInterface = {
  envs: [],
  menu: [],
  data: new Map(),
  isLoading: true,
  currentEnv: `${import.meta.env.VITE_API_SHARED_URL}`.match(/(dev|stage|prod)/)?.at(1) ?? 'dev',

  variables: {},

  currentApplication: null!,
  consumers: [],
  credentials: [],
  applications: [],

  setVariables: noop,
  setCurrentApplication: noop,
};

export const ExplorerContext = createContext<ExplorerContextInterface>(initialState);

export const ExplorerProvider = ({ children }: PropsWithChildren) => {
  const [, setStorageVariables] = useLocalStorage<Record<string, string>>('variables');
  const [value, setValue] = useState<ExplorerContextInterface>(initialState);
  const handleSetValue = (data: Partial<ExplorerContextInterface>) =>
    setValue((prevState) => ({ ...prevState, ...data }));
  useLoadSchema(handleSetValue);
  const [currentApplication, setCurrentApplication] = useState<string>(null!);
  const { consumers, credentials, applications } = useExplorerVarsHeader(
    currentApplication,
    get(value, ['variables', 'apiKey']),
  );

  const setVariables = (variables: Record<string, string>) => {
    if (Object.keys(variables).length > 0) {
      const clearVariables = Object.entries(variables).reduce(
        (acc, [key, value]) => ({
          ...acc,
          [key]: isString(value) ? value.trim() : value,
        }),
        {},
      );
      setValue((prevState) => ({ ...prevState, variables: { ...prevState.variables, ...clearVariables } }));
    }
  };

  const valueApplicationId = get(value, ['variables', 'applicationId']);

  useEffect(() => setStorageVariables(value.variables), [value.variables]);
  useEffect(() => setVariables({ applicationId: currentApplication }), [currentApplication]);
  useEffect(
    () =>
      void (!isString(currentApplication) && isString(valueApplicationId) && setCurrentApplication(valueApplicationId)),
    [currentApplication, valueApplicationId],
  );

  return (
    <ExplorerContext.Provider
      value={{
        ...value,
        currentApplication,
        currentEnv: initialState.currentEnv,
        consumers,
        credentials,
        applications,
        setVariables,
        setCurrentApplication,
      }}
    >
      {children}
    </ExplorerContext.Provider>
  );
};
